import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  BarChart3, Users, Eye, TrendingUp, DollarSign,
  FileText, Calendar, MessageSquare, Star, Lock,
  RefreshCw, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, subvalue, color = "blue" }) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
    <div className="flex items-center justify-between mb-4">
      <div className={`w-12 h-12 rounded-lg bg-${color}-50 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
    </div>
    <p className="text-3xl font-bold text-slate-900">{value}</p>
    <p className="text-sm text-slate-500">{label}</p>
    {subvalue && <p className="text-xs text-slate-400 mt-1">{subvalue}</p>}
  </div>
);

const AnalyticsDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [days, setDays] = useState("30");

  const authenticate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    
    try {
      const response = await axios.get(`${API}/analytics/dashboard`, {
        params: { password, days: parseInt(days) }
      });
      setData(response.data);
      setIsAuthenticated(true);
      
      // Also fetch realtime data
      const realtimeResponse = await axios.get(`${API}/analytics/realtime`, {
        params: { password }
      });
      setRealtimeData(realtimeResponse.data);
    } catch (err) {
      setError("Invalid password");
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/dashboard`, {
        params: { password, days: parseInt(days) }
      });
      setData(response.data);
      
      const realtimeResponse = await axios.get(`${API}/analytics/realtime`, {
        params: { password }
      });
      setRealtimeData(realtimeResponse.data);
    } catch (err) {
      console.error("Failed to refresh:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#0047AB]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#0047AB]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Analytics Dashboard</h1>
            <p className="text-slate-500 mt-2">Enter password to view site analytics</p>
          </div>
          
          <form onSubmit={authenticate} className="space-y-4">
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter analytics password"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="days">Time Period</Label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {error && (
              <p className="text-red-500 text-sm">{error}</p>
            )}
            
            <Button
              type="submit"
              className="w-full bg-[#0047AB] hover:bg-[#003380]"
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "View Analytics"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Analytics Dashboard</h1>
            <p className="text-slate-500">Tony Moves It - Site Performance</p>
          </div>
          <div className="flex items-center gap-4 mt-4 md:mt-0">
            <Select value={days} onValueChange={(v) => { setDays(v); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={refreshData} disabled={isLoading} variant="outline">
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Real-time Stats */}
        {realtimeData && (
          <div className="bg-gradient-to-r from-[#0047AB] to-[#003380] rounded-xl p-6 mb-8 text-white">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5" />
              <span className="font-semibold">Last 24 Hours</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-3xl font-bold">{realtimeData.last_24h_pageviews}</p>
                <p className="text-white/80 text-sm">Page Views</p>
              </div>
              <div>
                <p className="text-3xl font-bold">{realtimeData.unique_visitors_24h}</p>
                <p className="text-white/80 text-sm">Unique Visitors</p>
              </div>
              <div>
                <p className="text-3xl font-bold">{data?.business?.quotes_submitted || 0}</p>
                <p className="text-white/80 text-sm">Quotes ({days}d)</p>
              </div>
              <div>
                <p className="text-3xl font-bold">${(data?.business?.total_quote_value || 0).toLocaleString()}</p>
                <p className="text-white/80 text-sm">Quote Value ({days}d)</p>
              </div>
            </div>
          </div>
        )}

        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Eye}
            label="Total Page Views"
            value={data?.traffic?.total_pageviews || 0}
            subvalue={`Last ${days} days`}
          />
          <StatCard
            icon={Users}
            label="Unique Visitors"
            value={data?.traffic?.unique_visitors || 0}
            subvalue={`Last ${days} days`}
          />
          <StatCard
            icon={FileText}
            label="Quote Requests"
            value={data?.business?.quotes_submitted || 0}
          />
          <StatCard
            icon={Calendar}
            label="Bookings"
            value={data?.business?.bookings_made || 0}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Page Views by Page */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#0047AB]" />
              Page Views by Page
            </h3>
            <div className="space-y-3">
              {data?.traffic?.pages && Object.entries(data.traffic.pages)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 8)
                .map(([page, count]) => (
                  <div key={page} className="flex items-center justify-between">
                    <span className="text-slate-600 truncate max-w-[200px]">
                      {page === "/" ? "Home" : page.replace("/", "").charAt(0).toUpperCase() + page.slice(2)}
                    </span>
                    <div className="flex items-center gap-3">
                      <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#0047AB] rounded-full"
                          style={{ 
                            width: `${Math.min(100, (count / (data.traffic.total_pageviews || 1)) * 300)}%` 
                          }}
                        />
                      </div>
                      <span className="font-semibold text-slate-900 w-12 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              {(!data?.traffic?.pages || Object.keys(data.traffic.pages).length === 0) && (
                <p className="text-slate-500 text-center py-4">No page view data yet</p>
              )}
            </div>
          </div>

          {/* Top Referrers */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#0047AB]" />
              Traffic Sources
            </h3>
            <div className="space-y-3">
              {data?.traffic?.top_referrers && Object.entries(data.traffic.top_referrers)
                .slice(0, 8)
                .map(([source, count]) => (
                  <div key={source} className="flex items-center justify-between">
                    <span className="text-slate-600 truncate max-w-[200px]">
                      {source || "Direct"}
                    </span>
                    <span className="font-semibold text-slate-900">{count}</span>
                  </div>
                ))}
              {(!data?.traffic?.top_referrers || Object.keys(data.traffic.top_referrers).length === 0) && (
                <p className="text-slate-500 text-center py-4">No referrer data yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Business Metrics */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Recent Quotes */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Recent Quotes
            </h3>
            <div className="space-y-3">
              {data?.recent_activity?.quotes?.map((quote, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="font-medium text-slate-900">{quote.customer_name}</p>
                    <p className="text-xs text-slate-500">{formatDate(quote.created_at)}</p>
                  </div>
                  <span className="font-bold text-green-600">${quote.total_estimate?.toFixed(2)}</span>
                </div>
              ))}
              {(!data?.recent_activity?.quotes || data.recent_activity.quotes.length === 0) && (
                <p className="text-slate-500 text-center py-4">No quotes yet</p>
              )}
            </div>
          </div>

          {/* Recent Bookings */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#0047AB]" />
              Recent Bookings
            </h3>
            <div className="space-y-3">
              {data?.recent_activity?.bookings?.map((booking, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="font-medium text-slate-900">{booking.customer_name}</p>
                    <p className="text-xs text-slate-500">{formatDate(booking.created_at)}</p>
                  </div>
                  <span className="text-[#0047AB] font-medium">{booking.booking_date}</span>
                </div>
              ))}
              {(!data?.recent_activity?.bookings || data.recent_activity.bookings.length === 0) && (
                <p className="text-slate-500 text-center py-4">No bookings yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="font-bold text-lg text-slate-900 mb-4">Business Summary ({days} days)</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-[#0047AB]">{data?.business?.quotes_submitted || 0}</p>
              <p className="text-sm text-slate-500">Quotes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{data?.business?.bookings_made || 0}</p>
              <p className="text-sm text-slate-500">Bookings</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{data?.business?.contact_inquiries || 0}</p>
              <p className="text-sm text-slate-500">Inquiries</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{data?.business?.reviews_received || 0}</p>
              <p className="text-sm text-slate-500">Reviews</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">${(data?.business?.total_quote_value || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">Quote Value</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
